import { useCallback, useContext } from 'react';
import clsx from 'clsx';
import debounce from 'lodash.debounce';
import * as R from 'ramda';

import WorkOrderDatePicker from '@atom/components/common/workOrderDetail/workOrderFields/WorkOrderDatePicker';
import WorkOrderEnumMultipleField from '@atom/components/common/workOrderDetail/workOrderFields/WorkOrderEnumMultipleField';
import WorkOrderEnumMultipleFieldList from '@atom/components/common/workOrderDetail/workOrderFields/WorkOrderEnumMultipleFieldList';
import WorkOrderEnumSingleField from '@atom/components/common/workOrderDetail/workOrderFields/WorkOrderEnumSingleField';
import WorkOrderEnumSingleFieldList from '@atom/components/common/workOrderDetail/workOrderFields/WorkOrderEnumSingleFieldList';
import WorkOrderNumericField from '@atom/components/common/workOrderDetail/workOrderFields/WorkOrderNumericField';
import WorkOrderSummary from '@atom/components/common/workOrderDetail/workOrderFields/WorkOrderSummary';
import WorkOrderTextField from '@atom/components/common/workOrderDetail/workOrderFields/WorkOrderTextField';
import { DataType, FieldViewType } from '@atom/types/dataType';
import { WorkOrderField } from '@atom/types/work';
import {
  doesNotHaveRolePermissions,
  hasRolePermissions,
  ROLE_SETS,
} from '@atom/utilities/authUtilities';
import { CustomFieldView } from '@atom/utilities/taskUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';
import { getFieldPathById } from '@atom/utilities/workOrderFieldUtilities';
import {
  CLOSED_STATUS_ID,
  COMPLETED_STATUS_ID,
} from '@atom/utilities/workOrderStatusUtilities';

import BulkContext from '../../../BulkContext';
import { useWorkOrderValidation } from '../../../hooks/bulkWorkOrderHook';

import classes from './workInfo.module.css';

const DEBOUNCE_TIME = 1000;

const CustomFields = () => {
  const { workOrder, setWorkOrder, invalidFields } = useContext(BulkContext);
  const { validateWorkInfoField } = useWorkOrderValidation();
  const { fieldOrder, fields } = workOrder;

  const handleChange = (fieldId: string, value: any) => {
    // Get path to field (checking for nested cascading fields).
    const fieldPath = getFieldPathById(fieldId, fields);

    if (isNilOrEmpty(fieldPath)) {
      return;
    }

    const newWorkOrder = R.over(R.lensPath(fieldPath), field => ({
      ...field,
      value,
    }))(workOrder);

    setWorkOrder(newWorkOrder);
    validateWorkInfoField(fieldId, newWorkOrder);
  };

  const handleChangeDebounced = useCallback(
    debounce(handleChange, DEBOUNCE_TIME),
    [handleChange],
  );

  const showButton = hasRolePermissions(ROLE_SETS.INSPECTOR);

  const isRequiredOnCompleteStatus =
    workOrder.statusId === COMPLETED_STATUS_ID ||
    workOrder.statusId === CLOSED_STATUS_ID;

  const getContent = (field: WorkOrderField) => {
    const isFieldEditDisabled = doesNotHaveRolePermissions(ROLE_SETS.INSPECTOR);
    const requiredFieldIds = invalidFields.work_info || new Set([]);
    const isError = requiredFieldIds.has(field.id);

    // Fields marked `required` only need the "* " label treatment once the
    // work order is being created directly into a Completed/Closed status.
    const requiredFieldName = (
      isRequiredOnCompleteStatus && field.required
        ? 'required'
        : 'requiredOnCreate'
    ) as keyof WorkOrderField;

    const props = {
      field,
      error: isError,
      helperText: isError ? 'This field is required' : null,
      onChange: handleChange,
      isDisabled: isFieldEditDisabled,
      onBlur: handleChange,
      customFieldView: CustomFieldView.DETAIL,
      requiredFieldIds,
      requiredFieldName,
    };

    // Both requirements are validated on a Completed/Closed status, so the
    // multi-select label reflects whichever one asks for all options.
    const multiSelectRequiredFieldNames: (keyof WorkOrderField)[] =
      isRequiredOnCompleteStatus
        ? ['requiredOnCreate', 'required']
        : ['requiredOnCreate'];

    const hasSubFields = !isNilOrEmpty(field.subFields);

    const isListView = field.viewAs === FieldViewType.CHECKBOX;

    const components = {
      [DataType.SUMMARY]: <WorkOrderSummary {...props} />,
      [DataType.SHORT_TEXT]: (
        <WorkOrderTextField {...props} onChange={handleChangeDebounced} />
      ),
      [DataType.LONG_TEXT]: (
        <WorkOrderTextField {...props} onChange={handleChangeDebounced} />
      ),
      [DataType.DATE]: (
        <WorkOrderDatePicker {...props} onChange={handleChangeDebounced} />
      ),
      [DataType.DATE_TIME]: (
        <WorkOrderDatePicker {...props} onChange={handleChangeDebounced} />
      ),
      [DataType.ENUM_SINGLE]: hasSubFields ? (
        <div className={classes.enumSingleNestedContainer}>
          <WorkOrderEnumSingleField
            {...props}
            onChange={handleChangeDebounced}
          />
        </div>
      ) : isListView ? (
        <WorkOrderEnumSingleFieldList {...props} />
      ) : (
        <WorkOrderEnumSingleField {...props} onChange={handleChangeDebounced} />
      ),
      [DataType.ENUM_MULTIPLE]: isListView ? (
        <WorkOrderEnumMultipleFieldList
          {...props}
          requiredFieldNames={multiSelectRequiredFieldNames}
        />
      ) : (
        <WorkOrderEnumMultipleField
          {...props}
          requiredFieldNames={multiSelectRequiredFieldNames}
        />
      ),
      [DataType.NUMBER]: (
        <WorkOrderNumericField {...props} onChange={handleChangeDebounced} />
      ),
    };

    return (
      <div
        className={classes.fieldContainer}
        data-cy={`bulkCreateWorkInfoField-${field.title}`}
      >
        {components[field.dataType]}
      </div>
    );
  };

  const hideSection = !showButton && isNilOrEmpty(fields);
  return (
    <>
      <div className={classes.sectionHeader}>Custom Fields Section</div>
      <div
        className={clsx(classes.sectionContent, hideSection && classes.hide)}
      >
        {fieldOrder?.length === 0 && (
          <div className={classes.emptyList}>There are no custom fields.</div>
        )}
        {fieldOrder?.map((fieldId: string) => {
          const field = fields.find(item => item.id === fieldId);
          return getContent(field);
        })}
      </div>
    </>
  );
};

export default CustomFields;
